import React from 'react';
import {
  Stack,
  Button,
  Card,
  Typography,
  IconButton,
  TextField,
  Alert
} from '@mui/material';
import Iconify from '../minimal/iconify/Iconify';
import { useLocales } from '../../locales';

export const BreakTimesEditor = ({ breakTimes = [], onChange }) => {
  const { translate } = useLocales();

  const formatTimeDisplay = (time) => {
    const digits = String(time || '').replace(/\D/g, '');
    // Show partial input as-is while typing; only format complete HHMM values
    if (digits.length < 4) return digits;
    return `${digits.substring(0, 2)}:${digits.substring(2, 4)}`;
  };

  // Normalize a partial/invalid value to strict HHMM on blur:
  // "9"/"13" -> hour ("0900"/"1300"), "930" -> "0930", clamp to 23:59
  const normalizeTime = (time) => {
    const digits = String(time || '').replace(/\D/g, '').slice(0, 4);
    if (!digits) return '';
    const padded = digits.length <= 2 ? `${digits.padStart(2, '0')}00` : digits.padStart(4, '0');
    const hours = Math.min(parseInt(padded.substring(0, 2), 10), 23);
    const minutes = Math.min(parseInt(padded.substring(2, 4), 10), 59);
    return `${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`;
  };

  const handleAddBreakTime = () => {
    onChange([
      ...breakTimes,
      { startTime: "1200", endTime: "1300", label: translate('event_settings.lunch_break') }
    ]);
  };

  const handleRemoveBreakTime = (index) => {
    const newBreakTimes = [...breakTimes];
    newBreakTimes.splice(index, 1);
    onChange(newBreakTimes);
  };

  const handleBreakTimeChange = (index, field, value) => {
    const newBreakTimes = [...breakTimes];
    if (field === 'label') {
      newBreakTimes[index] = { ...newBreakTimes[index], [field]: value };
    } else {
      const cleanValue = value.replace(/[^0-9]/g, '');
      if (cleanValue.length <= 4) {
        newBreakTimes[index] = { ...newBreakTimes[index], [field]: cleanValue };
      }
    }
    onChange(newBreakTimes);
  };

  const handleBreakTimeBlur = (index, field) => {
    const normalized = normalizeTime(breakTimes[index]?.[field]);
    if (normalized === breakTimes[index]?.[field]) return;
    const newBreakTimes = [...breakTimes];
    newBreakTimes[index] = { ...newBreakTimes[index], [field]: normalized };
    onChange(newBreakTimes);
  };

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">
            {translate('event_settings.break_times')}
          </Typography>
          <Button
            size="small"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleAddBreakTime}
            variant="outlined"
          >
            {translate('event_settings.add_break')}
          </Button>
        </Stack>

        {breakTimes.length > 0 ? (
          breakTimes.map((breakTime, index) => (
            <Stack key={index} direction="row" spacing={2} alignItems="center">
              <TextField
                size="small"
                label={translate('event_settings.break_start')}
                value={formatTimeDisplay(breakTime.startTime)}
                onChange={(e) => handleBreakTimeChange(index, 'startTime', e.target.value)}
                onBlur={() => handleBreakTimeBlur(index, 'startTime')}
                sx={{ width: 100 }}
              />
              <Typography>-</Typography>
              <TextField
                size="small"
                label={translate('event_settings.break_end')}
                value={formatTimeDisplay(breakTime.endTime)}
                onChange={(e) => handleBreakTimeChange(index, 'endTime', e.target.value)}
                onBlur={() => handleBreakTimeBlur(index, 'endTime')}
                sx={{ width: 100 }}
              />
              <TextField
                size="small"
                label={translate('event_settings.break_label')}
                value={breakTime.label}
                onChange={(e) => handleBreakTimeChange(index, 'label', e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton
                color="error"
                onClick={() => handleRemoveBreakTime(index)}
                size="small"
              >
                <Iconify icon="eva:trash-2-outline" />
              </IconButton>
            </Stack>
          ))
        ) : (
          <Alert severity="info">
            {translate('event_settings.no_breaks')}
          </Alert>
        )}
      </Stack>
    </Card>
  );
};
